/**
 * Statistiques et bilan annuel.
 *
 * Toutes les agregations se font sur la **categorie racine** et en **charge
 * reelle** par defaut : c'est ce qui repond a « combien me coute ce poste ».
 */

import { somme, variation } from '../domain/argent'
import { anneeDe, moisDe, moisDeLAnnee } from '../domain/temps'
import type { Cents, DayKey, Id, MoisKey, Operation, Sens, Tiers } from '../domain/types'
import {
  categorieRacine,
  chargeReelle,
  perimetreCategorie,
  repartitionDe,
  type Index,
} from './donnees'
import { etatCreance, recuAu } from './repartition'

export type Mesure = 'charge' | 'flux'

function valeur(ix: Index, op: Operation, mesure: Mesure): Cents {
  return mesure === 'charge' ? chargeReelle(ix, op) : op.montant
}

export interface Filtre {
  du: DayKey
  au: DayKey
  compteId: Id | null
}

function selection(ix: Index, f: Filtre, sens: Sens): Operation[] {
  return ix.d.operations.filter(
    (op) => op.sens === sens && op.date >= f.du && op.date <= f.au && (f.compteId === null || op.compteId === f.compteId),
  )
}

// ---------------------------------------------------------------------------
// Repartition par categorie
// ---------------------------------------------------------------------------

export interface PosteCategorie {
  categorieId: Id
  montant: Cents
  part: number
  nbOperations: number
}

export function parCategorie(ix: Index, f: Filtre, sens: Sens, mesure: Mesure = 'charge'): PosteCategorie[] {
  const cumul = new Map<Id, { montant: Cents; n: number }>()
  for (const op of selection(ix, f, sens)) {
    const r = categorieRacine(ix, op.categorieId)
    const c = cumul.get(r) ?? { montant: 0, n: 0 }
    c.montant += valeur(ix, op, mesure)
    c.n++
    cumul.set(r, c)
  }
  const total = somme([...cumul.values()].map((c) => c.montant))
  return [...cumul.entries()]
    .map(([categorieId, c]) => ({
      categorieId,
      montant: c.montant,
      part: total > 0 ? c.montant / total : 0,
      nbOperations: c.n,
    }))
    .filter((p) => p.montant !== 0)
    .sort((a, b) => b.montant - a.montant)
}

// ---------------------------------------------------------------------------
// Serie mensuelle
// ---------------------------------------------------------------------------

export interface PointMensuel {
  mois: MoisKey
  depenses: Cents
  revenus: Cents
  solde: Cents
}

export function serieMensuelle(ix: Index, annee: number, compteId: Id | null, mesure: Mesure = 'charge'): PointMensuel[] {
  const points = new Map(moisDeLAnnee(annee).map((m) => [m, { depenses: 0, revenus: 0 }]))
  for (const op of ix.d.operations) {
    if (anneeDe(op.date) !== annee || (compteId !== null && op.compteId !== compteId)) continue
    const p = points.get(moisDe(op.date))
    if (!p) continue
    if (op.sens === 'depense') p.depenses += valeur(ix, op, mesure)
    else p.revenus += op.montant
  }
  return [...points.entries()].map(([mois, p]) => ({ mois, ...p, solde: p.revenus - p.depenses }))
}

// ---------------------------------------------------------------------------
// Comparaison N / N-1
// ---------------------------------------------------------------------------

export interface Comparaison {
  categorieId: Id
  actuel: Cents
  precedent: Cents
  ecart: Cents
  variation: number | null
}

/**
 * Compare l'annee `annee` a la precedente **sur la meme portion d'annee**.
 *
 * En septembre, comparer neuf mois de l'annee en cours a douze mois de l'an
 * dernier ferait croire a une baisse generale. On borne donc l'annee
 * precedente au meme jour calendaire que `auJour`.
 */
export function comparerAnnees(ix: Index, annee: number, auJour: DayKey, compteId: Id | null): Comparaison[] {
  const finActuelle = anneeDe(auJour) === annee ? auJour : `${annee}-12-31`
  const finPrecedente = `${annee - 1}${finActuelle.slice(4)}`.replace(/-02-29$/, '-02-28')
  const actuel = parCategorie(ix, { du: `${annee}-01-01`, au: finActuelle, compteId }, 'depense')
  const precedent = parCategorie(ix, { du: `${annee - 1}-01-01`, au: finPrecedente, compteId }, 'depense')

  const ids = new Set([...actuel, ...precedent].map((p) => p.categorieId))
  return [...ids]
    .map((categorieId) => {
      const a = actuel.find((p) => p.categorieId === categorieId)?.montant ?? 0
      const p = precedent.find((x) => x.categorieId === categorieId)?.montant ?? 0
      return { categorieId, actuel: a, precedent: p, ecart: a - p, variation: variation(a, p) }
    })
    .sort((x, y) => y.ecart - x.ecart)
}

// ---------------------------------------------------------------------------
// Bilan annuel d'une categorie
// ---------------------------------------------------------------------------

export interface DetteTiers {
  tiers: Tiers
  du: Cents
  encaisse: Cents
  restant: Cents
}

export interface LigneBilan {
  operation: Operation
  total: Cents
  partOrganismes: Cents
  partPersonnes: Cents
  partMoi: Cents
  ouvert: Cents
}

export interface BilanAnnuel {
  categorieId: Id
  annee: number
  auJour: DayKey
  nbOperations: number
  totalAvance: Cents
  /** Parts des assurances : dues et effectivement encaissees a `auJour`. */
  organismes: { du: Cents; encaisse: Cents; parTiers: DetteTiers[] }
  /** Parts refacturees a des personnes (l'ex-epouse). */
  personnes: { du: Cents; encaisse: Cents; parTiers: DetteTiers[] }
  resteTheorique: Cents
  creancesOuvertes: Cents
  resteEffectif: Cents
  lignes: LigneBilan[]
}

/**
 * Ce que la categorie m'a reellement coute sur l'annee.
 *
 *     total avance
 *   − part des assurances
 *   − part refacturee aux personnes
 *   = reste a charge theorique        (ce que je *devrais* supporter)
 *   + creances non encaissees a date
 *   = reste a charge effectif         (ce qui est vraiment sorti de ma poche)
 *
 * L'invariant `totalAvance − organismes.du − personnes.du = resteTheorique`
 * tient au centime, par construction de `calculerRepartition`.
 *
 * Seules les depenses sont comptees : un remboursement n'est pas un revenu de
 * la categorie, c'est le reglement d'une part.
 */
export function bilanAnnuel(ix: Index, categorieId: Id, annee: number, auJour: DayKey): BilanAnnuel {
  const perimetre = perimetreCategorie(ix, categorieId)
  const ops = ix.d.operations
    .filter((op) => op.sens === 'depense' && anneeDe(op.date) === annee && perimetre.has(op.categorieId))
    .sort((a, b) => a.date.localeCompare(b.date))

  const parTiers = new Map<Id, DetteTiers>()
  const lignes: LigneBilan[] = []

  for (const op of ops) {
    const r = repartitionDe(ix, op)
    const ligne: LigneBilan = { operation: op, total: op.montant, partOrganismes: 0, partPersonnes: 0, partMoi: r.partMoi, ouvert: 0 }

    for (const l of r.lignes) {
      if (l.part.tiersId === ix.d.moiId) continue
      const tiers = ix.tiersParId.get(l.part.tiersId)
      if (!tiers) continue
      const creance = etatCreance(l.part, l.montant, ix.reglementsParPart.get(l.part.id) ?? [])
      const encaisse = Math.min(recuAu(creance, auJour), l.montant)

      if (tiers.type === 'organisme') ligne.partOrganismes += l.montant
      else ligne.partPersonnes += l.montant
      ligne.ouvert += l.montant - encaisse

      const dette = parTiers.get(tiers.id) ?? { tiers, du: 0, encaisse: 0, restant: 0 }
      dette.du += l.montant
      dette.encaisse += encaisse
      dette.restant = dette.du - dette.encaisse
      parTiers.set(tiers.id, dette)
    }
    lignes.push(ligne)
  }

  const dettes = [...parTiers.values()].sort((a, b) => b.du - a.du)
  const groupe = (type: 'organisme' | 'personne') => {
    const t = dettes.filter((x) => (type === 'organisme' ? x.tiers.type === 'organisme' : x.tiers.type !== 'organisme'))
    return { du: somme(t.map((x) => x.du)), encaisse: somme(t.map((x) => x.encaisse)), parTiers: t }
  }

  const totalAvance = somme(lignes.map((l) => l.total))
  const organismes = groupe('organisme')
  const personnes = groupe('personne')
  const resteTheorique = somme(lignes.map((l) => l.partMoi))
  const creancesOuvertes = somme(lignes.map((l) => l.ouvert))

  return {
    categorieId,
    annee,
    auJour,
    nbOperations: lignes.length,
    totalAvance,
    organismes,
    personnes,
    resteTheorique,
    creancesOuvertes,
    resteEffectif: resteTheorique + creancesOuvertes,
    lignes,
  }
}
